// Code generated by OpenAPI Generator (https://openapi-generator.tech), manual changes will be lost - read more on https://github.com/algolia/api-clients-automation. DO NOT EDIT.

import type {
  AlgoliaHttpResponse,
  CreateClientOptions,
  Headers,
  Host,
  QueryParameters,
  Request,
  RequestOptions,
} from '@algolia/client-common';
import {
  createAuth,
  createIterablePromise,
  createTransporter,
  getAlgoliaAgent,
  shuffle,
  validateRequired,
} from '@algolia/client-common';

import type { BatchParams } from '../model/batchParams';
import type { Composition } from '../model/composition';
import type { CompositionRule } from '../model/compositionRule';
import type { GetTaskResponse } from '../model/getTaskResponse';
import type { ListCompositionsResponse } from '../model/listCompositionsResponse';
import type { MultipleBatchResponse } from '../model/multipleBatchResponse';
import type { RulesMultipleBatchResponse } from '../model/rulesMultipleBatchResponse';
import type { SearchCompositionRulesResponse } from '../model/searchCompositionRulesResponse';
import type { SearchForFacetValuesResponse } from '../model/searchForFacetValuesResponse';
import type { SearchResponse } from '../model/searchResponse';
import type { TaskIDResponse } from '../model/taskIDResponse';

import type {
  CustomDeleteProps,
  CustomGetProps,
  CustomPostProps,
  CustomPutProps,
  DeleteCompositionProps,
  DeleteCompositionRuleProps,
  GetCompositionProps,
  GetRuleProps,
  GetTaskProps,
  ListCompositionsProps,
  PutCompositionProps,
  PutCompositionRuleProps,
  SaveRulesProps,
  SearchCompositionRulesProps,
  SearchForFacetValuesProps,
  SearchProps,
  UpdateSortingStrategyCompositionProps,
  WaitForCompositionTaskOptions,
} from '../model/clientMethodProps';

export const apiClientVersion = '1.31.0';

function getDefaultHosts(appId: string): Host[] {
  return (
    [
      {
        url: `${appId}-dsn.algolia.net`,
        accept: 'read',
        protocol: 'https',
      },
      {
        url: `${appId}.algolia.net`,
        accept: 'write',
        protocol: 'https',
      },
    ] as Host[]
  ).concat(
    shuffle([
      {
        url: `${appId}-1.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
      {
        url: `${appId}-2.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
      {
        url: `${appId}-3.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
    ]),
  );
}

export function createCompositionClient({
  appId: appIdOption,
  apiKey: apiKeyOption,
  authMode,
  algoliaAgents,
  ...options
}: CreateClientOptions) {
  const auth = createAuth(appIdOption, apiKeyOption, authMode);
  const transporter = createTransporter({
    hosts: getDefaultHosts(appIdOption),
    ...options,
    algoliaAgent: getAlgoliaAgent({
      algoliaAgents,
      client: 'Composition',
      version: apiClientVersion,
    }),
    baseHeaders: {
      'content-type': 'text/plain',
      ...auth.headers(),
      ...options.baseHeaders,
    },
    baseQueryParameters: {
      ...auth.queryParameters(),
      ...options.baseQueryParameters,
    },
  });

  return {
    transporter,

    /**
     * The `appId` currently in use.
     */
    appId: appIdOption,

    /**
     * The `apiKey` currently in use.
     */
    apiKey: apiKeyOption,

    /**
     * Clears the cache of the transporter for the `requestsCache` and `responsesCache` properties.
     */
    clearCache(): Promise<void> {
      return Promise.all([transporter.requestsCache.clear(), transporter.responsesCache.clear()]).then(() => undefined);
    },

    /**
     * Get the value of the `algoliaAgent`, used by our libraries internally and telemetry system.
     */
    get _ua(): string {
      return transporter.algoliaAgent.value;
    },

    /**
     * Adds a `segment` to the `x-algolia-agent` sent with every requests.
     *
     * @param segment - The algolia agent (user-agent) segment to add.
     * @param version - The version of the agent.
     */
    addAlgoliaAgent(segment: string, version?: string | undefined): void {
      transporter.algoliaAgent.add({ segment, version });
    },

    /**
     * Helper method to switch the API key used to authenticate the requests.
     *
     * @param params - Method params.
     * @param params.apiKey - The new API Key to use.
     */
    setClientApiKey({ apiKey }: { apiKey: string }): void {
      if (!authMode || authMode === 'WithinHeaders') {
        transporter.baseHeaders['x-algolia-api-key'] = apiKey;
      } else {
        transporter.baseQueryParameters['x-algolia-api-key'] = apiKey;
      }
    },

    /**
     * Helper: Wait for a composition-level task to be published (completed) for a given `compositionID` and `taskID`.
     *
     * @summary Helper method that waits for a task to be published (completed).
     * @param WaitForCompositionTaskOptions - The `WaitForCompositionTaskOptions` object.
     * @param WaitForCompositionTaskOptions.compositionID - The `compositionID` where the operation was performed.
     * @param WaitForCompositionTaskOptions.taskID - The `taskID` returned in the method response.
     * @param WaitForCompositionTaskOptions.maxRetries - The maximum number of retries. 100 by default.
     * @param WaitForCompositionTaskOptions.timeout - The function to decide how long to wait between retries.
     * @param requestOptions - The requestOptions to send along with the query, they will be forwarded to the `getTask` method and merged with the transporter requestOptions.
     */
    waitForCompositionTask(
      {
        compositionID,
        taskID,
        maxRetries = 100,
        timeout = (retryCount: number): number => Math.min(retryCount * 200, 5000),
      }: WaitForCompositionTaskOptions,
      requestOptions?: RequestOptions,
    ): Promise<GetTaskResponse> {
      let retryCount = 0;

      return createIterablePromise({
        func: () => this.getTask({ compositionID, taskID }, requestOptions),
        validate: (response) => response.status === 'published',
        aggregator: () => (retryCount += 1),
        error: {
          validate: () => retryCount >= maxRetries,
          message: () =>
            `Stopped waiting for the task after ${maxRetries} retries. This does not mean the operation failed; it may still complete. If you need to keep polling, retry with a higher maxRetries.`,
        },
        timeout: () => timeout(retryCount),
      });
    },
    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customDelete - The customDelete object.
     * @param customDelete.path - Path of the endpoint, for example `1/newFeature`.
     * @param customDelete.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customDelete(
      { path, parameters }: CustomDeleteProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customDelete', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * This method lets you send requests to the Algolia REST API.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     * @param customDelete - The customDelete object.
     * @param customDelete.path - Path of the endpoint, for example `1/newFeature`.
     * @param customDelete.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see customDelete for the plain version.
     */
    customDeleteWithHTTPInfo(
      { path, parameters }: CustomDeleteProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<Record<string, unknown>>> {
      validateRequired('path', 'customDeleteWithHTTPInfo', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customGet - The customGet object.
     * @param customGet.path - Path of the endpoint, for example `1/newFeature`.
     * @param customGet.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customGet({ path, parameters }: CustomGetProps, requestOptions?: RequestOptions): Promise<Record<string, unknown>> {
      validateRequired('path', 'customGet', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * This method lets you send requests to the Algolia REST API.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     * @param customGet - The customGet object.
     * @param customGet.path - Path of the endpoint, for example `1/newFeature`.
     * @param customGet.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see customGet for the plain version.
     */
    customGetWithHTTPInfo(
      { path, parameters }: CustomGetProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<Record<string, unknown>>> {
      validateRequired('path', 'customGetWithHTTPInfo', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customPost - The customPost object.
     * @param customPost.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPost.parameters - Query parameters to apply to the current query.
     * @param customPost.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customPost(
      { path, parameters, body }: CustomPostProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customPost', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * This method lets you send requests to the Algolia REST API.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     * @param customPost - The customPost object.
     * @param customPost.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPost.parameters - Query parameters to apply to the current query.
     * @param customPost.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see customPost for the plain version.
     */
    customPostWithHTTPInfo(
      { path, parameters, body }: CustomPostProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<Record<string, unknown>>> {
      validateRequired('path', 'customPostWithHTTPInfo', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customPut - The customPut object.
     * @param customPut.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPut.parameters - Query parameters to apply to the current query.
     * @param customPut.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customPut(
      { path, parameters, body }: CustomPutProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customPut', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * This method lets you send requests to the Algolia REST API.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     * @param customPut - The customPut object.
     * @param customPut.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPut.parameters - Query parameters to apply to the current query.
     * @param customPut.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see customPut for the plain version.
     */
    customPutWithHTTPInfo(
      { path, parameters, body }: CustomPutProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<Record<string, unknown>>> {
      validateRequired('path', 'customPutWithHTTPInfo', path);

      const requestPath = '/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Delete a composition from the current Algolia application.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteComposition - The deleteComposition object.
     * @param deleteComposition.compositionID - Unique Composition ObjectID.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteComposition(
      { compositionID }: DeleteCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<TaskIDResponse> {
      validateRequired('compositionID', 'deleteComposition', compositionID);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Delete a composition from the current Algolia application.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteComposition - The deleteComposition object.
     * @param deleteComposition.compositionID - Unique Composition ObjectID.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see deleteComposition for the plain version.
     */
    deleteCompositionWithHTTPInfo(
      { compositionID }: DeleteCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<TaskIDResponse>> {
      validateRequired('compositionID', 'deleteCompositionWithHTTPInfo', compositionID);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Delete a Composition Rule from the specified Composition ID.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteCompositionRule - The deleteCompositionRule object.
     * @param deleteCompositionRule.compositionID - Unique Composition ObjectID.
     * @param deleteCompositionRule.objectID - Unique identifier of a rule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteCompositionRule(
      { compositionID, objectID }: DeleteCompositionRuleProps,
      requestOptions?: RequestOptions,
    ): Promise<TaskIDResponse> {
      validateRequired('compositionID', 'deleteCompositionRule', compositionID);

      validateRequired('objectID', 'deleteCompositionRule', objectID);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Delete a Composition Rule from the specified Composition ID.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteCompositionRule - The deleteCompositionRule object.
     * @param deleteCompositionRule.compositionID - Unique Composition ObjectID.
     * @param deleteCompositionRule.objectID - Unique identifier of a rule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see deleteCompositionRule for the plain version.
     */
    deleteCompositionRuleWithHTTPInfo(
      { compositionID, objectID }: DeleteCompositionRuleProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<TaskIDResponse>> {
      validateRequired('compositionID', 'deleteCompositionRuleWithHTTPInfo', compositionID);

      validateRequired('objectID', 'deleteCompositionRuleWithHTTPInfo', objectID);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Retrieve a single composition in the current Algolia application.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param getComposition - The getComposition object.
     * @param getComposition.compositionID - Unique Composition ObjectID.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getComposition({ compositionID }: GetCompositionProps, requestOptions?: RequestOptions): Promise<Composition> {
      validateRequired('compositionID', 'getComposition', compositionID);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Retrieve a single composition in the current Algolia application.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param getComposition - The getComposition object.
     * @param getComposition.compositionID - Unique Composition ObjectID.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see getComposition for the plain version.
     */
    getCompositionWithHTTPInfo(
      { compositionID }: GetCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<Composition>> {
      validateRequired('compositionID', 'getCompositionWithHTTPInfo', compositionID);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Retrieves a rule by its ID.  To find the object ID of a rule, use the [`search` operation](https://www.algolia.com/doc/rest-api/composition/search-composition-rules).
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param getRule - The getRule object.
     * @param getRule.compositionID - Unique Composition ObjectID.
     * @param getRule.objectID - Unique identifier of a rule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getRule({ compositionID, objectID }: GetRuleProps, requestOptions?: RequestOptions): Promise<CompositionRule> {
      validateRequired('compositionID', 'getRule', compositionID);

      validateRequired('objectID', 'getRule', objectID);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Retrieves a rule by its ID.  To find the object ID of a rule, use the [`search` operation](https://www.algolia.com/doc/rest-api/composition/search-composition-rules).
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param getRule - The getRule object.
     * @param getRule.compositionID - Unique Composition ObjectID.
     * @param getRule.objectID - Unique identifier of a rule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see getRule for the plain version.
     */
    getRuleWithHTTPInfo(
      { compositionID, objectID }: GetRuleProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<CompositionRule>> {
      validateRequired('compositionID', 'getRuleWithHTTPInfo', compositionID);

      validateRequired('objectID', 'getRuleWithHTTPInfo', objectID);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Checks the status of a given task.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     *  - addObject
     *  - deleteObject
     *  - deleteIndex
     * @param getTask - The getTask object.
     * @param getTask.compositionID - Unique Composition ObjectID.
     * @param getTask.taskID - Unique task identifier.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getTask({ compositionID, taskID }: GetTaskProps, requestOptions?: RequestOptions): Promise<GetTaskResponse> {
      validateRequired('compositionID', 'getTask', compositionID);

      validateRequired('taskID', 'getTask', taskID);

      const requestPath = '/1/compositions/{compositionID}/task/{taskID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{taskID}', encodeURIComponent(taskID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Checks the status of a given task.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     *  - addObject
     *  - deleteObject
     *  - deleteIndex
     * @param getTask - The getTask object.
     * @param getTask.compositionID - Unique Composition ObjectID.
     * @param getTask.taskID - Unique task identifier.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see getTask for the plain version.
     */
    getTaskWithHTTPInfo(
      { compositionID, taskID }: GetTaskProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<GetTaskResponse>> {
      validateRequired('compositionID', 'getTaskWithHTTPInfo', compositionID);

      validateRequired('taskID', 'getTaskWithHTTPInfo', taskID);

      const requestPath = '/1/compositions/{compositionID}/task/{taskID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{taskID}', encodeURIComponent(taskID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Lists all compositions in the current Algolia application.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param listCompositions - The listCompositions object.
     * @param listCompositions.page - Requested page of the API response. If `null`, the API response is not paginated.
     * @param listCompositions.hitsPerPage - Number of hits per page.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listCompositions(
      { page, hitsPerPage }: ListCompositionsProps = {},
      requestOptions: RequestOptions | undefined = undefined,
    ): Promise<ListCompositionsResponse> {
      const requestPath = '/1/compositions';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (hitsPerPage !== undefined) {
        queryParameters['hitsPerPage'] = hitsPerPage.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Lists all compositions in the current Algolia application.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     *  - settings
     * @param listCompositions - The listCompositions object.
     * @param listCompositions.page - Requested page of the API response. If `null`, the API response is not paginated.
     * @param listCompositions.hitsPerPage - Number of hits per page.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see listCompositions for the plain version.
     */
    listCompositionsWithHTTPInfo(
      { page, hitsPerPage }: ListCompositionsProps = {},
      requestOptions: RequestOptions | undefined = undefined,
    ): Promise<AlgoliaHttpResponse<ListCompositionsResponse>> {
      const requestPath = '/1/compositions';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (hitsPerPage !== undefined) {
        queryParameters['hitsPerPage'] = hitsPerPage.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Adds, updates, or deletes compositions with a single API request.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param batchParams - The batchParams object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    multipleBatch(batchParams: BatchParams, requestOptions?: RequestOptions): Promise<MultipleBatchResponse> {
      validateRequired('batchParams', 'multipleBatch', batchParams);

      validateRequired('batchParams.requests', 'multipleBatch', batchParams.requests);

      const requestPath = '/1/compositions/*/batch';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: batchParams,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Adds, updates, or deletes compositions with a single API request.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param batchParams - The batchParams object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see multipleBatch for the plain version.
     */
    multipleBatchWithHTTPInfo(
      batchParams: BatchParams,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<MultipleBatchResponse>> {
      validateRequired('batchParams', 'multipleBatchWithHTTPInfo', batchParams);

      validateRequired('batchParams.requests', 'multipleBatchWithHTTPInfo', batchParams.requests);

      const requestPath = '/1/compositions/*/batch';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: batchParams,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Update and insert a composition in the current Algolia application.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param putComposition - The putComposition object.
     * @param putComposition.compositionID - Unique Composition ObjectID.
     * @param putComposition.composition - The composition object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    putComposition(
      { compositionID, composition }: PutCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<TaskIDResponse> {
      validateRequired('compositionID', 'putComposition', compositionID);

      validateRequired('composition', 'putComposition', composition);

      validateRequired('composition.objectID', 'putComposition', composition.objectID);
      validateRequired('composition.name', 'putComposition', composition.name);
      validateRequired('composition.behavior', 'putComposition', composition.behavior);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: composition,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Update and insert a composition in the current Algolia application.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param putComposition - The putComposition object.
     * @param putComposition.compositionID - Unique Composition ObjectID.
     * @param putComposition.composition - The composition object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see putComposition for the plain version.
     */
    putCompositionWithHTTPInfo(
      { compositionID, composition }: PutCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<TaskIDResponse>> {
      validateRequired('compositionID', 'putCompositionWithHTTPInfo', compositionID);

      validateRequired('composition', 'putCompositionWithHTTPInfo', composition);

      validateRequired('composition.objectID', 'putCompositionWithHTTPInfo', composition.objectID);
      validateRequired('composition.name', 'putCompositionWithHTTPInfo', composition.name);
      validateRequired('composition.behavior', 'putCompositionWithHTTPInfo', composition.behavior);

      const requestPath = '/1/compositions/{compositionID}'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: composition,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * If a composition rule with the provided ID already exists, it\'s replaced. Otherwise, a new one is added.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param putCompositionRule - The putCompositionRule object.
     * @param putCompositionRule.compositionID - Unique Composition ObjectID.
     * @param putCompositionRule.objectID - Unique identifier of a rule object.
     * @param putCompositionRule.compositionRule - The compositionRule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    putCompositionRule(
      { compositionID, objectID, compositionRule }: PutCompositionRuleProps,
      requestOptions?: RequestOptions,
    ): Promise<TaskIDResponse> {
      validateRequired('compositionID', 'putCompositionRule', compositionID);

      validateRequired('objectID', 'putCompositionRule', objectID);

      validateRequired('compositionRule', 'putCompositionRule', compositionRule);

      validateRequired('compositionRule.objectID', 'putCompositionRule', compositionRule.objectID);
      validateRequired('compositionRule.consequence', 'putCompositionRule', compositionRule.consequence);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: compositionRule,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * If a composition rule with the provided ID already exists, it\'s replaced. Otherwise, a new one is added.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param putCompositionRule - The putCompositionRule object.
     * @param putCompositionRule.compositionID - Unique Composition ObjectID.
     * @param putCompositionRule.objectID - Unique identifier of a rule object.
     * @param putCompositionRule.compositionRule - The compositionRule object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see putCompositionRule for the plain version.
     */
    putCompositionRuleWithHTTPInfo(
      { compositionID, objectID, compositionRule }: PutCompositionRuleProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<TaskIDResponse>> {
      validateRequired('compositionID', 'putCompositionRuleWithHTTPInfo', compositionID);

      validateRequired('objectID', 'putCompositionRuleWithHTTPInfo', objectID);

      validateRequired('compositionRule', 'putCompositionRuleWithHTTPInfo', compositionRule);

      validateRequired('compositionRule.objectID', 'putCompositionRuleWithHTTPInfo', compositionRule.objectID);
      validateRequired('compositionRule.consequence', 'putCompositionRuleWithHTTPInfo', compositionRule.consequence);

      const requestPath = '/1/compositions/{compositionID}/rules/{objectID}'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{objectID}', encodeURIComponent(objectID));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: compositionRule,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Create or update or delete multiple composition rules.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param saveRules - The saveRules object.
     * @param saveRules.compositionID - Unique Composition ObjectID.
     * @param saveRules.rules - The rules object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    saveRules(
      { compositionID, rules }: SaveRulesProps,
      requestOptions?: RequestOptions,
    ): Promise<RulesMultipleBatchResponse> {
      validateRequired('compositionID', 'saveRules', compositionID);

      validateRequired('rules', 'saveRules', rules);

      const requestPath = '/1/compositions/{compositionID}/rules/batch'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: rules,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Create or update or delete multiple composition rules.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param saveRules - The saveRules object.
     * @param saveRules.compositionID - Unique Composition ObjectID.
     * @param saveRules.rules - The rules object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see saveRules for the plain version.
     */
    saveRulesWithHTTPInfo(
      { compositionID, rules }: SaveRulesProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<RulesMultipleBatchResponse>> {
      validateRequired('compositionID', 'saveRulesWithHTTPInfo', compositionID);

      validateRequired('rules', 'saveRulesWithHTTPInfo', rules);

      const requestPath = '/1/compositions/{compositionID}/rules/batch'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: rules,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Runs a query on a single composition and returns matching results.
     *
     * Required API Key ACLs:
     *  - search
     * @param search - The search object.
     * @param search.compositionID - Unique Composition ObjectID.
     * @param search.requestBody - The requestBody object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    search<T>(
      { compositionID, requestBody }: SearchProps,
      requestOptions?: RequestOptions,
    ): Promise<SearchResponse<T>> {
      validateRequired('compositionID', 'search', compositionID);

      validateRequired('requestBody', 'search', requestBody);

      const requestPath = '/1/compositions/{compositionID}/run'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: requestBody,
        useReadTransporter: true,
        cacheable: true,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Runs a query on a single composition and returns matching results.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - search
     * @param search - The search object.
     * @param search.compositionID - Unique Composition ObjectID.
     * @param search.requestBody - The requestBody object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see search for the plain version.
     */
    searchWithHTTPInfo<T>(
      { compositionID, requestBody }: SearchProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<SearchResponse<T>>> {
      validateRequired('compositionID', 'searchWithHTTPInfo', compositionID);

      validateRequired('requestBody', 'searchWithHTTPInfo', requestBody);

      const requestPath = '/1/compositions/{compositionID}/run'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: requestBody,
        useReadTransporter: true,
        cacheable: true,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Searches for composition rules in your index.
     *
     * Required API Key ACLs:
     *  - settings
     * @param searchCompositionRules - The searchCompositionRules object.
     * @param searchCompositionRules.compositionID - Unique Composition ObjectID.
     * @param searchCompositionRules.searchCompositionRulesParams - The searchCompositionRulesParams object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    searchCompositionRules(
      { compositionID, searchCompositionRulesParams }: SearchCompositionRulesProps,
      requestOptions?: RequestOptions,
    ): Promise<SearchCompositionRulesResponse> {
      validateRequired('compositionID', 'searchCompositionRules', compositionID);

      const requestPath = '/1/compositions/{compositionID}/rules/search'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: searchCompositionRulesParams ? searchCompositionRulesParams : {},
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Searches for composition rules in your index.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - settings
     * @param searchCompositionRules - The searchCompositionRules object.
     * @param searchCompositionRules.compositionID - Unique Composition ObjectID.
     * @param searchCompositionRules.searchCompositionRulesParams - The searchCompositionRulesParams object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see searchCompositionRules for the plain version.
     */
    searchCompositionRulesWithHTTPInfo(
      { compositionID, searchCompositionRulesParams }: SearchCompositionRulesProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<SearchCompositionRulesResponse>> {
      validateRequired('compositionID', 'searchCompositionRulesWithHTTPInfo', compositionID);

      const requestPath = '/1/compositions/{compositionID}/rules/search'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: searchCompositionRulesParams ? searchCompositionRulesParams : {},
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Searches for values of a specified facet attribute on the composition\'s main source\'s index.  - By default, facet values are sorted by decreasing count.   You can adjust this with the `sortFacetValueBy` parameter. - Searching for facet values doesn\'t work if you have **more than 65 searchable facets and searchable attributes combined**.
     *
     * Required API Key ACLs:
     *  - search
     * @param searchForFacetValues - The searchForFacetValues object.
     * @param searchForFacetValues.compositionID - Unique Composition ObjectID.
     * @param searchForFacetValues.facetName - Facet attribute in which to search for values.  This attribute must be included in the `attributesForFaceting` index setting with the `searchable()` modifier.
     * @param searchForFacetValues.searchForFacetValuesRequest - The searchForFacetValuesRequest object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    searchForFacetValues(
      { compositionID, facetName, searchForFacetValuesRequest }: SearchForFacetValuesProps,
      requestOptions?: RequestOptions,
    ): Promise<SearchForFacetValuesResponse> {
      validateRequired('compositionID', 'searchForFacetValues', compositionID);

      validateRequired('facetName', 'searchForFacetValues', facetName);

      const requestPath = '/1/compositions/{compositionID}/facets/{facetName}/query'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{facetName}', encodeURIComponent(facetName));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: searchForFacetValuesRequest ? searchForFacetValuesRequest : {},
        useReadTransporter: true,
        cacheable: true,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Searches for values of a specified facet attribute on the composition\'s main source\'s index.  - By default, facet values are sorted by decreasing count.   You can adjust this with the `sortFacetValueBy` parameter. - Searching for facet values doesn\'t work if you have **more than 65 searchable facets and searchable attributes combined**.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - search
     * @param searchForFacetValues - The searchForFacetValues object.
     * @param searchForFacetValues.compositionID - Unique Composition ObjectID.
     * @param searchForFacetValues.facetName - Facet attribute in which to search for values.  This attribute must be included in the `attributesForFaceting` index setting with the `searchable()` modifier.
     * @param searchForFacetValues.searchForFacetValuesRequest - The searchForFacetValuesRequest object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see searchForFacetValues for the plain version.
     */
    searchForFacetValuesWithHTTPInfo(
      { compositionID, facetName, searchForFacetValuesRequest }: SearchForFacetValuesProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<SearchForFacetValuesResponse>> {
      validateRequired('compositionID', 'searchForFacetValuesWithHTTPInfo', compositionID);

      validateRequired('facetName', 'searchForFacetValuesWithHTTPInfo', facetName);

      const requestPath = '/1/compositions/{compositionID}/facets/{facetName}/query'
        .replace('{compositionID}', encodeURIComponent(compositionID))
        .replace('{facetName}', encodeURIComponent(facetName));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: searchForFacetValuesRequest ? searchForFacetValuesRequest : {},
        useReadTransporter: true,
        cacheable: true,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },

    /**
     * Updates the \"sortingStrategy\" field of an existing composition. This endpoint lets you create a new sorting strategy mapping or replace the configured one. The provided sorting indices must be associated indices or replicas of the main targeted index.  This endpoint can\'t validate whether the sort index is related to the composition\'s main index. Validation fails at runtime if the index you updated isn\'t related.  The update is applied to the specified composition within the current Algolia application and returns a taskID that can be used to track the operation’s completion.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param updateSortingStrategyComposition - The updateSortingStrategyComposition object.
     * @param updateSortingStrategyComposition.compositionID - Unique Composition ObjectID.
     * @param updateSortingStrategyComposition.requestBody - The requestBody object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    updateSortingStrategyComposition(
      { compositionID, requestBody }: UpdateSortingStrategyCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<TaskIDResponse> {
      validateRequired('compositionID', 'updateSortingStrategyComposition', compositionID);

      validateRequired('requestBody', 'updateSortingStrategyComposition', requestBody);

      const requestPath = '/1/compositions/{compositionID}/sortingStrategy'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: requestBody,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Updates the \"sortingStrategy\" field of an existing composition. This endpoint lets you create a new sorting strategy mapping or replace the configured one. The provided sorting indices must be associated indices or replicas of the main targeted index.  This endpoint can\'t validate whether the sort index is related to the composition\'s main index. Validation fails at runtime if the index you updated isn\'t related.  The update is applied to the specified composition within the current Algolia application and returns a taskID that can be used to track the operation’s completion.
     *
     * Resolves with the full HTTP response information: status code, headers (when the requester captures them), raw body and deserialized data. Bypasses the requests and responses caches: always performs the API call.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param updateSortingStrategyComposition - The updateSortingStrategyComposition object.
     * @param updateSortingStrategyComposition.compositionID - Unique Composition ObjectID.
     * @param updateSortingStrategyComposition.requestBody - The requestBody object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     * @see updateSortingStrategyComposition for the plain version.
     */
    updateSortingStrategyCompositionWithHTTPInfo(
      { compositionID, requestBody }: UpdateSortingStrategyCompositionProps,
      requestOptions?: RequestOptions,
    ): Promise<AlgoliaHttpResponse<TaskIDResponse>> {
      validateRequired('compositionID', 'updateSortingStrategyCompositionWithHTTPInfo', compositionID);

      validateRequired('requestBody', 'updateSortingStrategyCompositionWithHTTPInfo', requestBody);

      const requestPath = '/1/compositions/{compositionID}/sortingStrategy'.replace(
        '{compositionID}',
        encodeURIComponent(compositionID),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: requestBody,
      };

      return transporter.requestWithHttpInfo(request, requestOptions);
    },
  };
}
